import moment from "moment";
import "moment/locale/tr";
import React, { useEffect, useState } from "react";
import Button from "../../../components/Button";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Icon from "../../../components/Icon";
import LabelValue from "../../../components/LabelValue";
import Loading from "../../../components/Loading/Loading";
import Modal from "../../../components/Modal/Modal";
import ModalConfirmation from "../../../components/Modal/ModalConfirmation";
import Pill from "../../../components/Pill";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { parameterNames, processNames } from "../../../constants/translations";
import { API_GET } from "../../../helpers/api";
import {
  convertAmountToFrontend,
  convertNumberToFrontend,
} from "../../../helpers/convert";
import { setNotification } from "../../../helpers/notification";
import { MODAL_STRUCTURE } from "./parametersModalStructure";
import { REQUEST } from "./parametersRequests";
import { REQUEST_VALIDATORS } from "./parametersValidators";

moment.locale("tr");

export default function SystemParameters({ t }) {
  // const serverTime = useSelector((state) => state.utilities.serverTime);
  let timer1 = null;
  const [intervalState, setIntervalState] = useState(null);
  const [activeTab, setActiveTab] = useState("parameter");
  const [editedType, setEditedType] = useState("");
  const [editedSubType, setEditedSubType] = useState("");
  const [editOrAddModal, setEditOrAddModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [inputs, setInputs] = useState({});
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [processData, setProcessData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [offDayData, setOffDayData] = useState([]);
  const [parameterData, setParameterData] = useState([]);

  const [contractData, setContractData] = useState([]);
  const [selectedSession, setSelectedSession] = useState({});
  const [selectedContract, setSelectedContract] = useState({});
  const [selectedOffDay, setSelectedOffDay] = useState({});
  const [selectedProcess, setSelectedProcess] = useState({});
  const [selectedParameter, setSelectedParameter] = useState({});

  const [parameterHistory, setParameterHistory] = useState([]);
  const [parameterHistoryModal, setParameterHistoryModal] = useState(false);
  const [processHistory, setProcessHistory] = useState([]);
  const [processHistoryModal, setProcessHistoryModal] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [sessionHistoryModal, setSessionHistoryModal] = useState(false);
  const [sessionToggleLoading, setSessionToggleLoading] = useState(false);
  const [contractToggleLoading, setContractToggleLoading] = useState(false);
  const parameterTranslations = parameterNames(t);
  const processTranslations = processNames(t);

  // pill styles
  const yekgProcessStatusTypes = {
    OPEN: "success",
    CLOSED: "important",
    WAITING: "default",
  };
  const sessionProcessTypes = {
    ACTIVE: "success",
    PASSIVE: "important",
    CLOSED: "important",
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps

    setInterval(function () {
      API_GET("system-date-time/full").then((res) => {
        const time = res.data;
        setIntervalState(time);
      });
    }, 5000);

    return () => {
      clearInterval(timer1);
    };
  }, []);

  // columns
  const parametersColumns = [
    {
      name: t("pages.default.systemParameters.table.parameters"),
      selector: "parameterName",
      sortable: false,
      cell: (row) => {
        return parameterTranslations[row.parameterName];
      },
    },
    {
      name: t("pages.default.systemParameters.table.values"),
      selector: "parameterValue",
      sortable: false,
      cell: (row) => {
        return row.parameterDateValue !== null
          ? moment(row.parameterDateValue).format("DD.MM.YYYY")
          : row.parameterDecimalValue !== null
          ? convertNumberToFrontend({
              num: row.parameterDecimalValue,
              digit: 2,
            })
          : convertAmountToFrontend(row.parameterValue);
      },
    },
    {
      name: t("pages.default.systemParameters.table.process"),
      selector: "id",
      sortable: false,
      width: "140px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectParameter(row)}
            />
            <Button
              title={t("ui.default.edit")}
              suffixIcon="edit"
              onClick={() => editParameters(row)}
              type="primary"
            />
          </div>
        );
      },
    },
  ];
  const processColumns = [
    {
      name: t("pages.default.systemParameters.table.processName"),
      selector: "processName",
      sortable: false,
      cell: (row) => {
        return processTranslations[row.processName];
      },
    },
    {
      name: t("pages.default.systemParameters.table.startHour"),
      selector: "startHour",
      sortable: false,
      cell: (row) => {
        return (
          <div>
            {row.startHour.toString().length === 1
              ? `0${row.startHour}`
              : row.startHour}
            :
            {row.startMinute.toString().length === 1
              ? `0${row.startMinute}`
              : row.startMinute}
          </div>
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.endHour"),
      selector: "endHour",
      sortable: false,
      cell: (row) => {
        return (
          <div>
            {row.endHour.toString().length === 1
              ? `0${row.endHour}`
              : row.endHour}
            :
            {row.endMinute.toString().length === 1
              ? `0${row.endMinute}`
              : row.endMinute}
          </div>
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.status"),
      selector: "yekgProcessStatus",
      sortable: false,
      cell: (row) => {
        return (
          <div>
            <Pill
              label={t(`ui.default.${row.yekgProcessStatus}`)}
              type={yekgProcessStatusTypes[row.yekgProcessStatus]}
            />
          </div>
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.process"),
      selector: "id",
      sortable: false,
      width: "140px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.review")}
              suffixIcon="search"
              type="secondary"
              onClick={() => inspectProcess(row)}
            />
            <Button
              title={t("ui.default.edit")}
              suffixIcon="edit"
              type="primary"
              onClick={() => editProcess(row)}
            />
          </div>
        );
      },
    },
  ];
  const offDaysColumns = [
    {
      name: t("pages.default.systemParameters.table.offDayName"),
      selector: "offDayName",
      sortable: false,
    },
    {
      name: t("pages.default.systemParameters.table.startHour"),
      selector: "startDate",
      sortable: false,
      cell: (row) => {
        return <div>{moment(row.startDate).format("DD.MM.YYYY")}</div>;
      },
    },
    {
      name: t("pages.default.systemParameters.table.endHour"),
      selector: "endDate",
      sortable: false,
      cell: (row) => {
        return <div>{moment(row.endDate).format("DD.MM.YYYY")}</div>;
      },
    },
    {
      name: t("pages.default.systemParameters.table.process"),
      selector: "id",
      sortable: false,
      width: "200px",
      cell: (row) => {
        return (
          <div style={{ display: "flex" }}>
            <Button
              title={t("ui.default.edit")}
              suffixIcon="edit"
              type="primary"
              onClick={() => editOffDay(row)}
            />
            <Button
              title={t("ui.default.delete")}
              suffixIcon="minus"
              type="destructive"
              onClick={() => deleteOffDay(row)}
            />
          </div>
        );
      },
    },
  ];
  const sessionColumns = [
    {
      name: t("pages.default.systemParameters.table.organizedMarketTime"),
      selector: "sessionDate",
      sortable: false,
      cell: (row) => {
        return (
          <div>
            {moment(row.sessionDate)
              .locale(window.localStorage.i18nextLng)
              .format("DD.MM.YYYY - dddd")}
          </div>
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.minimumPrice"),
      selector: "minimumPrice",
      sortable: false,
      cell: (row) => {
        return (
          convertNumberToFrontend({ num: row.minimumPrice, digit: 2 }) || 0
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.maximumPrice"),
      selector: "maximumPrice",
      sortable: false,
      cell: (row) => {
        return (
          convertNumberToFrontend({ num: row.maximumPrice, digit: 2 }) || 0
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.creationType"),
      selector: "yekgSessionCreationType",
      sortable: false,
      cell: (row) => {
        return row.yekgSessionCreationType
          ? t(`ui.default.codes.${row.yekgSessionCreationType}`)
          : "";
      },
    },
    {
      name: t("pages.default.systemParameters.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        return (
          <Pill
            type={sessionProcessTypes[row.status]}
            label={t(`ui.default.codes.${row.status}`)}
          />
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.process"),
      selector: "id",
      sortable: false,
      width: "150px",
      cell: (row) => {
        return (
          <div className="km__p--session-columns-process">
            <Button
              type="secondary"
              suffixIcon="search"
              onClick={() => inspectSession(row)}
            />
            {moment(row.sessionDate).diff(intervalState, "days") >= 0 && (
              <>
                <Button
                  title={t("ui.default.edit")}
                  type="primary"
                  suffixIcon="edit"
                  onClick={() => editSession(row)}
                />
                <ToggleSwitch
                  onChange={(e) => {
                    sessionProcessChange(e, row);
                  }}
                  checked={row.status === "ACTIVE" ? true : false}
                  disabled={sessionToggleLoading}
                />
              </>
            )}
          </div>
        );
      },
    },
  ];
  const contractColumns = [
    {
      name: t("pages.default.systemParameters.table.contractName"),
      selector: "contractName",
      sortable: false,
    },
    {
      name: t("pages.default.systemParameters.table.resourceType"),
      selector: "type",
      sortable: false,
      cell: (row) => {
        return t(`ui.default.codes.${row.type}`);
      },
    },
    {
      name: t("pages.default.systemParameters.table.organizedMarketTime"),
      selector: "sessionId",
      sortable: false,
      cell: (row) => {
        return moment(selectedSession.sessionDate)
          .locale(window.localStorage.i18nextLng)
          .format("DD.MM.YYYY - dddd");
      },
    },
    {
      name: t("pages.default.systemParameters.table.status"),
      selector: "status",
      sortable: false,
      cell: (row) => {
        return (
          <Pill
            type={sessionProcessTypes[row.status]}
            label={t(`ui.default.codes.${row.status}`)}
          />
        );
      },
    },
    {
      name: t("pages.default.systemParameters.table.process"),
      selector: "id",
      sortable: false,
      width: "100px",
      cell: (row) => {
        return (
          <div className="km__p--session-columns-process">
            {moment(selectedSession.sessionDate).diff(intervalState, "days") >=
              0 &&
              row.status !== "CLOSED" && (
                <>
                  {row.totalAmount > 0 ? null : (
                    <Button
                      title={t("ui.default.edit")}
                      type="primary"
                      suffixIcon="edit"
                      onClick={() => editContract(row)}
                    />
                  )}
                  <ToggleSwitch
                    onChange={(e) => {
                      contractProcessChange(e, row);
                    }}
                    checked={row.status === "ACTIVE" ? true : false}
                    disabled={contractToggleLoading}
                  />
                </>
              )}
          </div>
        );
      },
    },
  ];

  const REQUEST_SUCCESS = {
    PARAMETERS: {
      add: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.parameterAdd")
        );
        return getParameter();
      },
      edit: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.parameterEdit")
        );
        return getParameter();
      },
    },
    PROCESS: {
      add: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.processAdd")
        );
        return getProcess();
      },
      edit: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.processEdit")
        );
        return getProcess();
      },
    },
    OFF_DAY: {
      add: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.offDayAdd")
        );
        return getOffDay();
      },
      edit: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.offDayEdit")
        );
        return getOffDay();
      },
      delete: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.offDayDelete")
        );
        return getOffDay();
      },
    },
    SESSION: {
      add: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.sessionAdd")
        );
        return getSession();
      },
      edit: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.sessionEdit")
        );
        return getSession();
      },
    },
    CONTRACT: {
      add: function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.contractAdd")
        );
        getSession();
        return getSession();
      },
      edit: async function () {
        setNotification(
          "success",
          t("pages.default.systemParameters.notifications.contractEdit")
        );
        return getSession();
      },
    },
  };

  useEffect(() => {
    getSystemParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // CONTRACT update olduğunda yeni sessionları alıp contratın güncellenmesi
    if (selectedSession.id) {
      let session = sessionData.filter((item) => {
        return item.id === selectedSession.id;
      })[0];
      sessionSelection(session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  const getSystemParameters = async () => {
    await getProcess();
    await getSession();
    await getOffDay();
    await getParameter();
    setPageLoading(false);
  };

  const getProcess = async () => {
    try {
      let res = await API_GET("system-parameter/process");
      setProcessData(res.data);
    } catch (e) {
      setNotification("error", e.response.data.message);
    }
  };
  const getSession = async () => {
    try {
      let res = await API_GET("system-parameter/session");
      setSessionData(res.data.items);
    } catch (e) {
      setNotification("error", e.response.data.message);
    }
  };
  const getOffDay = async () => {
    try {
      let res = await API_GET("system-parameter/off-day");
      setOffDayData(res.data);
    } catch (e) {
      setNotification("error", e.response.data.message);
    }
  };

  const checkParameterNameIsEmpty = (paramName) => {
    return parameterTranslations[paramName] === undefined;
  };

  const getParameter = async () => {
    var obj = [];
    try {
      let res = await API_GET("system-parameter/parameter");
      for (let p of res.data) {
        if (!checkParameterNameIsEmpty(p.parameterName)) {
          obj.push(p);
        }
      }
      setParameterData(obj);
    } catch (e) {
      setNotification("error", e.response.data.message);
    }
  };
  const sessionProcessChange = async (e, row) => {
    setSessionToggleLoading(true);
    try {
      await REQUEST.SESSION.edit({
        inputs: {
          sessionDate: row.sessionDate,
          minimumPrice: row.minimumPrice ? row.minimumPrice : 0,
          maximumPrice: row.maximumPrice,
          status: row.status === "ACTIVE" ? "PASSIVE" : "ACTIVE",
          description: `Seans acil durum butonu ile ${
            row.status === "ACTIVE" ? "Pasif" : "Aktif"
          } duruma getirildi.`,
          yekgSessionCreationType: row.yekgSessionCreationType,
        },
        sessionId: row.id,
      });
      await REQUEST_SUCCESS.SESSION.edit();
      setSessionToggleLoading(false);
    } catch (err) {
      setNotification("error", err.response.data.message);
      setSessionToggleLoading(false);
    }
  };
  const contractProcessChange = async (e, row) => {
    setContractToggleLoading(true);
    try {
      await REQUEST.CONTRACT.edit({
        inputs: {
          status: row.status === "ACTIVE" ? "PASSIVE" : "ACTIVE",
          type: row.type,
          contractName: row.contractName,
          description: `Kontrat acil durum butonu ile ${
            row.status === "ACTIVE" ? "Pasif" : "Aktif"
          } duruma getirildi.`,
        },
        contractId: row.id,
      });
      await REQUEST_SUCCESS.CONTRACT.edit();
      setContractToggleLoading(false);
    } catch (err) {
      setNotification("error", err.response.data.message);
      setContractToggleLoading(false);
    }
  };

  const systemParameterRequest = async () => {
    setLoading(true);
    try {
      await REQUEST[editedType][editedSubType]({
        inputs: inputs,
        sessionId: selectedSession.id,
        contractId: selectedContract.id,
        offDayId: selectedOffDay.id,
        processId: selectedProcess.id,
        parameterId: selectedParameter.id,
        parameterName: selectedParameter.parameterName,
      });
      await REQUEST_SUCCESS[editedType][editedSubType]();
      setInputs({});
      setSelectedContract({});
      setConfirmationModal(false);
      editOrAddModalToggle();
      setEditedType();
      setEditedSubType();
      setLoading(false);
    } catch (err) {
      if (
        err.response.data.message ===
        "There is already a contract with given resource type"
      ) {
        setNotification(
          "error",
          t("pages.default.systemParameters.error.contractAlreadyHave")
        );
        setLoading(false);
      } else {
        setNotification("error", err.response.data.message);
        setLoading(false);
      }
    }
  };

  const inspectParameter = async (row) => {
    try {
      let res = await API_GET(
        `system-parameter/parameter/${row.parameterName}/history`
      );
      setParameterHistory(res.data);
      setParameterHistoryModal(true);
    } catch (err) {
      setNotification("error", err.response.data.message);
    }
  };
  const inspectProcess = async (row) => {
    try {
      let res = await API_GET(`system-parameter/process/${row.id}/history`);
      setProcessHistory(res.data);
      setProcessHistoryModal(true);
    } catch (err) {
      setNotification("error", err.response.data.message);
    }
  };
  const inspectSession = async (row) => {
    try {
      let res = await API_GET(`system-parameter/session/${row.id}/history`);
      setSessionHistory(res.data);
      setSessionHistoryModal(true);
    } catch (err) {
      setNotification("error", err.response.data.message);
    }
  };
  const editOrAddModalToggle = () => {
    if (editOrAddModal) {
      setEditOrAddModal(false);
      setEditedType("");
      setEditedSubType("");
      return;
    }
    setEditOrAddModal(true);
  };
  const toggleConfirmation = () => {
    if (
      REQUEST_VALIDATORS[editedType][editedSubType]({
        inputs: inputs,
        offDays: offDayData,
        process: processData,
        parameters: parameterData,
        t: t,
      })
    ) {
      setConfirmationModal(true);
    }
  };

  const inputChange = (e) => {
    if (e.target.name === "offDayStartDate" || e.target.name === "startDate") {
      setInputs({
        ...inputs,
        startDate: moment(e.target.value).format("YYYY-MM-DD"),
      });
    }
    if (e.target.name === "offDayEndDate" || e.target.name === "endDate") {
      setInputs({
        ...inputs,
        endDate: moment(e.target.value).format("YYYY-MM-DD"),
      });
    }
    if (e.target.name === "sessionDate")
      setInputs({
        ...inputs,
        sessionDate: moment(e.target.value).format("YYYYMMDD"),
      });
    if (
      e.target.name !== "offDayEndDate" &&
      e.target.name !== "offDayStartDate" &&
      e.target.name !== "sessionDate"
    )
      setInputs({
        ...inputs,
        [e.target.name]: e.target.value,
      });
    if (e.target.name === "startTime")
      setInputs({
        ...inputs,
        startHour: e.target.value.split(":")[0],
        startMinute: e.target.value.split(":")[1],
      });
    if (e.target.name === "endTime")
      setInputs({
        ...inputs,
        endHour: e.target.value.split(":")[0],
        endMinute: e.target.value.split(":")[1],
      });
  };

  const sessionSelection = (row) => {
    setSelectedSession(row);
    API_GET(`system-parameter/contract`, {
      sessionId: row.id,
    }).then((res) => {
      setContractData(res.data.items);
    });
  };
  const sessionSelected = (row) => {
    if (row.id === selectedSession.id) return true;
    return false;
  };

  const editParameters = (row) => {
    setEditedType("PARAMETERS");
    setEditedSubType("edit");
    setEditOrAddModal(true);
    setInputs({
      parameterName: row.parameterName,
      parameterValue: row.parameterValue,
      parameterDecimalValue: row.parameterDecimalValue,
      parameterDateValue: row.parameterDateValue,
      startDate: row.startDate,
      description: "",
    });
    setSelectedParameter(row);
  };

  const editProcess = (row) => {
    setEditedType("PROCESS");
    setEditedSubType("edit");
    setEditOrAddModal(true);
    setInputs({
      processName: row.processName,
      startMinute: row.startMinute,
      startHour: row.startHour,
      endMinute: row.endMinute,
      endHour: row.endHour,
      description: "",
    });
    setSelectedProcess(row);
  };

  const addOffDay = () => {
    setEditedType("OFF_DAY");
    setEditedSubType("add");
    setEditOrAddModal(true);
    setInputs({});
  };
  const editOffDay = (row) => {
    setEditedType("OFF_DAY");
    setEditedSubType("edit");
    setEditOrAddModal(true);
    setInputs({
      offDayName: row.offDayName,
      startDate: row.startDate,
      endDate: row.endDate,
    });
    setSelectedOffDay(row);
  };
  const deleteOffDay = (row) => {
    setEditedType("OFF_DAY");
    setEditedSubType("delete");
    setSelectedOffDay(row);
    setConfirmationModal(true);
  };

  const addSession = () => {
    let defaultMin, defaultMax;
    defaultMin = parameterData.find((item) => {
      return item.parameterName === "organized_market_default_minimum_price";
    }).parameterValue;
    defaultMax = parameterData.find((item) => {
      return item.parameterName === "organized_market_default_maximum_price";
    }).parameterValue;

    setEditedType("SESSION");
    setEditedSubType("add");
    setEditOrAddModal(true);
    setInputs({
      yekgSessionCreationType: "MANUEL",
      minimumPrice: defaultMin,
      maximumPrice: defaultMax,
    });
  };
  const editSession = (row) => {
    setEditedType("SESSION");
    setEditedSubType("edit");
    setEditOrAddModal(true);
    setInputs({
      sessionDate: row.sessionDate,
      minimumPrice: row.minimumPrice,
      maximumPrice: row.maximumPrice,
      status: row.status,
      description: "",
      yekgSessionCreationType: row.yekgSessionCreationType,
    });
    sessionSelection(row);
  };

  const addContract = () => {
    setEditedType("CONTRACT");
    setEditedSubType("add");
    setEditOrAddModal(true);
    setInputs({ sessionId: selectedSession.id });
  };
  const editContract = (row) => {
    setEditedType("CONTRACT");
    setEditedSubType("edit");
    setEditOrAddModal(true);
    setInputs({
      type: row.type,
      status: row.status,
      contractName: row.contractName,
    });
    setSelectedContract(row);
  };
  const parameterValueParser = (item) => {
    if (item.parameterDateValue) {
      return moment(item.parameterDateValue).format("DD.MM.YYYY");
    } else {
      return (
        item.parameterValue ||
        item.parameterDecimalValue ||
        item.parameterStringValue
      );
    }
  };
  const processValueParser = (item) => {
    let start = `${
      item.startHour.toString().length === 1
        ? `0${item.startHour}`
        : item.startHour
    }:${
      item.startMinute.toString().length === 1
        ? `0${item.startMinute}`
        : item.startMinute
    }`;
    let end = `${
      item.endHour.toString().length === 1 ? `0${item.endHour}` : item.endHour
    }:${
      item.endMinute.toString().length === 1
        ? `0${item.endMinute}`
        : item.endMinute
    }`;
    return `${start} - ${end}`;
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.systemParameters.title")}
      </h5>
      <div className="km__p--system-parameters-tabs">
        <div
          className={`km__p--system-parameters-tab ${
            activeTab === "parameter"
              ? "km__p--system-parameters-tab-is-active"
              : ""
          }`}
          onClick={() => setActiveTab("parameter")}
        >
          {t("pages.default.systemParameters.parameterProcess")}
        </div>
        <div
          className={`km__p--system-parameters-tab ${
            activeTab === "organizedMarket"
              ? "km__p--system-parameters-tab-is-active"
              : ""
          }`}
          onClick={() => setActiveTab("organizedMarket")}
        >
          {t("pages.default.systemParameters.organizedMarketProcess")}
        </div>
      </div>
      {pageLoading && (
        <Loading text={t("pages.default.systemParameters.loadingParams")} />
      )}
      {activeTab === "parameter" && !pageLoading && (
        <>
          {/* Parameter */}
          <div className="km__p--card">
            <div className="km__p--system-parameters-content">
              <DataTable
                columns={parametersColumns}
                data={parameterData}
                rowKey="id"
                defaultSortAsc={true}
                // defaultSortedField="indexOrder"
              />
            </div>
          </div>
          {/* Process */}
          <div className="km__p--card">
            <div className="km__p--system-parameters-content">
              <div className="km__p--system-parameters-content-title">
                <p>{t("pages.default.systemParameters.table.processName")}</p>
              </div>
              <DataTable
                columns={processColumns}
                data={processData}
                rowKey="id"
                defaultSortAsc={false}
                defaultSortedField={"id"}
              />
            </div>
          </div>
          {/* Off Days */}
          <div className="km__p--card">
            <div className="km__p--system-parameters-content">
              <div className="km__p--system-parameters-content-title">
                <p>{t("pages.default.systemParameters.offDays")}</p>
                <Button
                  label={t("pages.default.systemParameters.addOffDay")}
                  type="primary"
                  onClick={addOffDay}
                />
              </div>
              <DataTable
                columns={offDaysColumns}
                data={offDayData}
                rowKey="id"
                defaultSortAsc={false}
                defaultSortedField={"id"}
              />
            </div>
          </div>
        </>
      )}
      {activeTab === "organizedMarket" && !pageLoading && (
        <>
          <div className="km__p--card">
            <div className="km__p--system-parameters-content">
              <div className="km__p--system-parameters-content-title">
                <p>
                  {t("pages.default.systemParameters.organizedMarketSessions")}
                </p>
                <Button
                  label={t("pages.default.systemParameters.addSession")}
                  type="primary"
                  onClick={addSession}
                />
              </div>
              <DataTable
                columns={sessionColumns}
                data={sessionData}
                selectableRow
                rowSelection={(row) => sessionSelection(row)}
                isRowSelected={(row) => sessionSelected(row)}
                rowKey="id"
                defaultSortAsc={false}
                defaultSortedField={"sessionDate"}
              />
            </div>
          </div>
          {selectedSession.id && (
            <div className="km__p--card">
              <div className="km__p--system-parameters-content">
                <div className="km__p--system-parameters-content-title">
                  <p>{t("pages.default.systemParameters.contractProcess")}</p>
                  <Button
                    label={t("pages.default.systemParameters.addContract")}
                    type="primary"
                    onClick={addContract}
                  />
                </div>
                <DataTable
                  columns={contractColumns}
                  data={contractData}
                  rowKey="id"
                  defaultSortAsc={false}
                  defaultSortedField={"id"}
                />
              </div>
            </div>
          )}
        </>
      )}
      {/* Edit or Add Modal */}
      {editedType && (
        <Modal isOpen={editOrAddModal} toggle={editOrAddModalToggle}>
          {MODAL_STRUCTURE[editedType][editedSubType]({
            dismiss: editOrAddModalToggle,
            save: toggleConfirmation,
            inputChange: inputChange,
            session: selectedSession,
            inputs: inputs,
            t: t,
            parameterName: inputs.parameterName,
            processName: inputs.processName,
            contract: selectedContract,
          })}
        </Modal>
      )}
      <Modal
        isOpen={parameterHistoryModal}
        toggle={() => setParameterHistoryModal(false)}
      >
        <div className="km__p--parameter-history-title">
          {t("pages.default.systemParameters.modals.parameterProcessHistory")}
          <div>
            <Icon
              name="x"
              size="16"
              onClick={() => setParameterHistoryModal(false)}
            />
          </div>
        </div>
        <div className="km__p--parameter-history-wrapper">
          {parameterHistory.length > 0 &&
            parameterHistory
              .slice(0)
              .reverse()
              .map((item, index) => {
                return (
                  <div className="km__p--parameter-history-body" key={index}>
                    <div>
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.process"
                        )}
                        value={parameterHistory?.length - index}
                      />
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.parameterName"
                        )}
                        value={parameterTranslations[item.parameterName]}
                      />
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.parameterValue"
                        )}
                        value={
                          item.parameterName === "market_reconciliation"
                            ? moment(item.parameterDateValue).format(
                                "DD.MM.YYYY"
                              )
                            : item.parameterName === "PIU"
                            ? convertAmountToFrontend(
                                item.parameterDecimalValue
                              )
                            : convertAmountToFrontend(item.parameterValue)
                        }
                      />
                    </div>
                    <div>
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.processDate"
                        )}
                        value={moment(item.processDate).format(
                          "DD.MM.YYYY HH:mm:ss"
                        )}
                      />
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.description"
                        )}
                        value={item.description}
                      />
                      <LabelValue
                        label={t(
                          "pages.default.systemParameters.modals.startDate"
                        )}
                        value={moment(item.startDate).format(
                          "DD.MM.YYYY HH:mm:ss"
                        )}
                      />
                    </div>
                  </div>
                );
              })}
          {parameterHistory.length === 0 && (
            <div style={{ padding: "10px 20px" }}>
              <EmptyState
                icon="file-text"
                text={t(
                  "pages.default.systemParameters.modals.processHistoryNotFound"
                )}
              />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={processHistoryModal}
        toggle={() => setProcessHistoryModal(false)}
      >
        <div className="km__p--parameter-history-title">
          {t("pages.default.systemParameters.modals.processHistoryTitle")}
          <div>
            <Icon
              name="x"
              size="16"
              onClick={() => setProcessHistoryModal(false)}
            />
          </div>
        </div>
        <div className="km__p--parameter-history-wrapper">
          {processHistory.length > 0 &&
            processHistory.map((item, index) => {
              return (
                <div className="km__p--parameter-history-body" key={index}>
                  <div>
                    <LabelValue
                      label={t("pages.default.systemParameters.modals.process")}
                      value={index + 1}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.processName"
                      )}
                      value={processTranslations[item.processName]}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.processValue"
                      )}
                      value={processValueParser(item)}
                    />
                  </div>
                  <div>
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.processDate"
                      )}
                      value={moment(item.processDate).format(
                        "DD.MM.YYYY HH:mm:ss"
                      )}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.description"
                      )}
                      value={item.description}
                    />
                  </div>
                </div>
              );
            })}
          {processHistory.length === 0 && (
            <div style={{ padding: "10px 20px" }}>
              <EmptyState
                icon="file-text"
                text={t(
                  "pages.default.systemParameters.modals.processHistoryNotFound"
                )}
              />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={sessionHistoryModal}
        toggle={() => setSessionHistoryModal(false)}
      >
        <div className="km__p--parameter-history-title">
          {t("pages.default.systemParameters.modals.sessionHistoryNotFound")}
          <div>
            <Icon
              name="x"
              size="16"
              onClick={() => setSessionHistoryModal(false)}
            />
          </div>
        </div>
        <div className="km__p--parameter-history-wrapper">
          {sessionHistory.length > 0 &&
            sessionHistory.map((item, index) => {
              return (
                <div className="km__p--parameter-history-body" key={index}>
                  <div>
                    <LabelValue
                      label={t("pages.default.systemParameters.modals.process")}
                      value={index + 1}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.sessionDate"
                      )}
                      value={moment(item.sessionDate)
                        .locale(window.localStorage.i18nextLng)
                        .format("DD.MM.YYYY - dddd")}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.minPrice"
                      )}
                      value={item.minimumPrice}
                    />
                    <LabelValue
                      label={t("pages.default.systemParameters.modals.status")}
                      value={t(`ui.default.codes.${item.status}`)}
                    />
                  </div>
                  <div>
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.processDate"
                      )}
                      value={moment(item.modifiedAt).format(
                        "DD.MM.YYYY HH:mm:ss"
                      )}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.description"
                      )}
                      value={item.description}
                    />
                    <LabelValue
                      label={t(
                        "pages.default.systemParameters.modals.maxPrice"
                      )}
                      value={item.maximumPrice}
                    />
                  </div>
                </div>
              );
            })}
          {sessionHistory.length === 0 && (
            <div style={{ padding: "10px 20px" }}>
              <EmptyState
                icon="file-text"
                text={t(
                  "pages.default.systemParameters.modals.processHistoryNotFound"
                )}
              />
            </div>
          )}
        </div>
      </Modal>
      {/* Confirmation Modal */}
      <ModalConfirmation
        isOpen={confirmationModal}
        toggle={() => setConfirmationModal(false)}
        header={t("pages.default.systemParameters.modals.confirmationTitle")}
        body={t("pages.default.systemParameters.modals.confirmationBody")}
        approve={systemParameterRequest}
        loading={loading}
      />
    </>
  );
}
